import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Action} from 'platform/components';
import {match} from 'ts-pattern';

import {useLocation} from 'react-router-dom';

import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {interestRoutes, testIds, workshopRoutes} from '@dms/routes';
import {Main, PageTab, usePermissions, usePersonnelOverviewOpen} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {SalesInterestOverview} from './(sections)/SalesInterestOverview/SalesInterestOverview';
import {WorkshopInterestOverview} from './(sections)/WorkshopInterestOverview/WorkshopInterestOverview';

const SALES_TAB_ID = 'sales';
const WORKSHOP_TAB_ID = 'workshop';

export function InterestOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInterestPersonnelPanelOpen, toggleInterestPersonnelPanel] =
    usePersonnelOverviewOpen('interest');
  const [isWorkshopPersonnelPanelOpen, toggleWorkshopPersonnelPanel] =
    usePersonnelOverviewOpen('workshop-interest');

  const isWorkshopInterestEnabled = isFeatureEnabled(featureFlags.WORKSHOP_INTEREST);

  const [
    canReadBusinessCase,
    canReadInterest,
    canCreateInterest,
    canReadWorkshopInterest,
    canCreateWorkshopInterest,
  ] = usePermissions({
    permissionKeys: [
      'businessCaseRead',
      'interestRead',
      'interestCreate',
      'workshopInterestRead',
      'workshopInterestCreate',
    ],
    scopes: {
      interestRead: {
        // no need for scopes, general read without specified participation
        participation: 'SKIP',
        // no need for scopes, general read without specified branch
        branchId: 'SKIP',
      },
      interestCreate: {
        // no need for scopes, general creation without specified participation
        participation: 'SKIP',
        // no need for scopes, general creation without specified branch
        branchId: 'SKIP',
      },
      workshopInterestRead: {
        // no need for scopes, general read without specified participation
        participation: 'SKIP',
        // no need for scopes, general read without specified branch
        branchId: 'SKIP',
      },
      workshopInterestCreate: {
        // no need for scopes, general creation without specified participation
        participation: 'SKIP',
        // no need for scopes, general creation without specified branch
        branchId: 'SKIP',
      },
    },
  });

  const hasSalesAccess = canReadBusinessCase && canReadInterest;
  const hasWorkshopAccess = canReadWorkshopInterest && isWorkshopInterestEnabled;

  const tabs = buildArray<PageTab>()
    .when(hasSalesAccess, {
      id: SALES_TAB_ID,
      title: i18n.t('page.interests.labels.salesTab'),
      href: interestRoutes.overview,
      content: (
        <SalesInterestOverview
          isPersonnelPanelOpen={isInterestPersonnelPanelOpen}
          canViewInterest={canReadInterest}
        />
      ),
      'data-testid': testIds.interest.overview('salesTab'),
    })
    .when(hasWorkshopAccess, {
      id: WORKSHOP_TAB_ID,
      title: i18n.t('page.interests.labels.workshopTab'),
      href: interestRoutes.workshopOverview,
      content: (
        <WorkshopInterestOverview
          isPersonnelPanelOpen={isWorkshopPersonnelPanelOpen}
          canViewWorkshopInterest={hasWorkshopAccess}
        />
      ),
      'data-testid': testIds.interest.overview('workshopTab'),
    });

  // When both tabs are available the active section follows the URL. When only one
  // tab survives the ACL checks the tab bar is hidden, so the single available
  // section is always the active one regardless of the current path.
  const hasMultipleTabs = tabs.length > 1;
  const activeTab = tabs.find((tab) => tab.href === location.pathname) ?? tabs[0];
  const isWorkshopTabActive = activeTab?.id === WORKSHOP_TAB_ID;
  const isSalesTabActive = activeTab?.id === SALES_TAB_ID;

  const canShowPersonnelPanel =
    (canReadInterest && isSalesTabActive) || (hasWorkshopAccess && isWorkshopTabActive);

  const {isActivePersonnelPanelOpen, toggleActivePersonnelPanel, activePersonnelPanelTestId} =
    match(isSalesTabActive)
      .with(true, () => ({
        isActivePersonnelPanelOpen: isInterestPersonnelPanelOpen,
        toggleActivePersonnelPanel: toggleInterestPersonnelPanel,
        activePersonnelPanelTestId: testIds.interest.overview('toggleInterestPersonnelPanel'),
      }))
      .with(false, () => ({
        isActivePersonnelPanelOpen: isWorkshopPersonnelPanelOpen,
        toggleActivePersonnelPanel: toggleWorkshopPersonnelPanel,
        activePersonnelPanelTestId: testIds.interest.overview('toggleWorkshopPersonnelPanel'),
      }))
      .exhaustive();

  const actions = buildArray<Action>()
    .when(canShowPersonnelPanel, {
      type: 'iconButtonWithTooltip',
      icon: 'action/vertical_split',
      priority: isActivePersonnelPanelOpen ? 'info' : 'secondary',
      onClick: toggleActivePersonnelPanel,
      tooltip: {label: i18n.t('page.personnelOverview.title')},
      'data-testid': activePersonnelPanelTestId,
    })
    .when(isSalesTabActive && canCreateInterest, {
      type: 'button',
      onClick: () => navigate(interestRoutes.create),
      variant: 'primary',
      'data-testid': testIds.interest.overview('create'),
      title: i18n.t('entity.interest.actions.newInterest'),
    })
    .when(isWorkshopTabActive, {
      type: 'button',
      onClick: () => navigate(workshopRoutes.interestCreate),
      variant: 'primary',
      // The button stays visible but is disabled when the user lacks the
      // WORKSHOP_INTEREST/CREATE permission, otherwise navigating would bounce
      // them to the restricted area.
      isDisabled: !canCreateWorkshopInterest,
      tooltip: !canCreateWorkshopInterest ? i18n.t('general.labels.noPermission') : undefined,
      'data-testid': testIds.interest.overview('create'),
      title: i18n.t('entity.interest.actions.newInterest'),
    });

  return (
    <>
      <Helmet title={i18n.t('page.interests.title')} />
      <Main
        isFullHeight
        actions={actions}
        tabs={hasMultipleTabs ? tabs : undefined}
        data-testid={testIds.interest.overview('container')}
      >
        {!hasMultipleTabs ? tabs[0]?.content : null}
      </Main>
    </>
  );
}
